import { ChangeEvent, useState } from 'react'

import {
  IconTrash,
  IconPlus,
  IconInfoCircle,
  IconClockHour4,
} from '@tabler/icons-react'
import { Menu, Skeleton, Space, Tooltip } from 'antd'
import styled from 'styled-components'

import {
  calculateHours,
  calculateTotalHours,
  cloneValue,
} from './workerAttendanceHelpers'
import { WorkerAttendanceModal } from './WorkerAttendanceModal'

import { Button } from '@/components/shared-components/BUI/Button'
import { Input } from '@/components/shared-components/BUI/Input'
import { Select } from '@/components/shared-components/BUI/Select'
import { Text } from '@/components/shared-components/BUI/Text'
import Ellipsis from '@/components/shared-components/EllipsisDropdown'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import {
  AttendanceValue,
  HourConfig,
} from '@/modules/templates/types/blueprint'
import { pluralise } from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

import { SubcontractorsByProjectUiQuery } from 'types/api.graphql'

type SubcontractorAttendance =
  AttendanceValue['subcontractorAttendances'][number]

const HoursButton = styled(Button)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Heading = styled.div<{ smallScreen?: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 3rem;
  flex-direction: row;
  ${(props) =>
    props.smallScreen &&
    `
    gap: 1rem;
    flex-direction: column;
  `}
`

const ellipsisMenu = (onDelete) => (
  <Menu>
    <Menu.Item danger onClick={onDelete} key="remove">
      <IconTrash size={16} /> Remove
    </Menu.Item>
  </Menu>
)

const getSubbieSuffixIcon = (subbie: SubcontractorAttendance) => {
  const missingSignOffs = subbie.workerAttendance?.some(
    (att) => !!att.signedOnAt && !att.signedOffAt
  )

  if (!missingSignOffs) return <IconClockHour4 size={16} />

  return (
    <Tooltip title="One or more workers have not signed off for the day.">
      <IconInfoCircle
        size={16}
        color={buildPassTheme.colour.semantic.warningDark}
      />
    </Tooltip>
  )
}

type Props = {
  value?: AttendanceValue
  onChange?: (value: AttendanceValue) => void
  projectSubcontractors: SubcontractorsByProjectUiQuery['subcontractorsByProject']
  projectWorkers: Array<{ id: string; subcontractorId: string; name: string }>
  hourConfig: HourConfig[]
}

export const CaptureWorkerAttendance: React.FC<Props> = ({
  value,
  onChange,
  projectSubcontractors,
  projectWorkers,
  hourConfig,
}) => {
  const smallScreen = useBreakpoint('md')
  const [selectedSubcontractorId, setSelectedSubcontractorId] =
    useState<string>(undefined)

  if (!value || !projectSubcontractors) return <Skeleton />

  const addSubcontractor = () => {
    const newValue = cloneValue(value)
    newValue.subcontractorAttendances.push({
      manuallyCreated: true,
      workerAttendance: [],
    } as SubcontractorAttendance)
    onChange(newValue)
  }

  const deleteSubbie = (index: number) => () => {
    const newValue = cloneValue(value)
    newValue.subcontractorAttendances.splice(index, 1)
    onChange(newValue)
  }

  const changeSubcontractor = (index: number) => (subbieId: string) => {
    const newValue = cloneValue(value)
    newValue.subcontractorAttendances[index] = {
      subcontractorId: subbieId,
      workerAttendance: [],
      manuallyCreated: true,
    }
    onChange(newValue)
  }

  const setSubcontractorComments =
    (subcontractorId: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = cloneValue(value)

      const subbie = newValue.subcontractorAttendances.find(
        (s) => s.subcontractorId === subcontractorId
      )

      subbie.comments = e.target.value

      onChange(newValue)
    }

  const openModal = (subcontractorId: string) =>
    setSelectedSubcontractorId(subcontractorId)

  const closeModal = () => setSelectedSubcontractorId(undefined)

  const getAvailableSubcontractors = (currentSubcontractorId: string) => {
    const subcontractorIdsInUse = value.subcontractorAttendances.map(
      (s) => s.subcontractorId
    )

    return projectSubcontractors.filter(
      (s) =>
        !subcontractorIdsInUse.includes(s.id) || s.id === currentSubcontractorId
    )
  }

  return (
    <>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Heading smallScreen={smallScreen}>
          <Text secondary="_600">
            Sign ons have been automatically pulled in and grouped by
            subcontractor
          </Text>
        </Heading>
        <Flexbox flexDirection="column">
          <Space direction="vertical" size={smallScreen ? 'large' : 'middle'}>
            {value.subcontractorAttendances.map((subbie, index) => {
              const totalHours = calculateHours(subbie, value.overrides)

              return (
                <Flexbox
                  key={`${subbie.subcontractorId}-${index}`}
                  flexDirection="column"
                  gap="0.5rem"
                  md={{
                    flexDirection: 'row',
                    gap: '1rem',
                    $fullWidth: true,
                  }}
                >
                  <Flexbox
                    style={{ flexGrow: 1, flexBasis: '80%' }}
                    alignItems="center"
                    gap="0.5rem"
                  >
                    {subbie.manuallyCreated ? (
                      <Select
                        style={{ flexBasis: '50%' }}
                        value={subbie.subcontractorId}
                        onChange={changeSubcontractor(index)}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          ((option as { children?: string })?.children ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {getAvailableSubcontractors(subbie.subcontractorId).map(
                          (s) => (
                            <Select.Option value={s.id} key={s.id}>
                              {s.company.name}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    ) : (
                      <div style={{ flexBasis: '50%' }}>
                        {
                          projectSubcontractors.find(
                            (s) => s.id === subbie.subcontractorId
                          )?.company?.name
                        }
                      </div>
                    )}

                    <Input
                      style={{ flexBasis: '50%' }}
                      placeholder="Comments"
                      defaultValue={
                        value?.subcontractorAttendances?.find(
                          (s) => s.subcontractorId === subbie.subcontractorId
                        )?.comments || ''
                      }
                      onChange={setSubcontractorComments(
                        subbie.subcontractorId
                      )}
                    />
                  </Flexbox>

                  <Flexbox
                    alignItems="center"
                    gap="0.5rem"
                    style={{ flexBasis: '20%' }}
                  >
                    <HoursButton
                      icon={getSubbieSuffixIcon(subbie)}
                      disabled={!subbie.subcontractorId}
                      onClick={() => openModal(subbie.subcontractorId)}
                    >
                      <span>{pluralise(totalHours, 'hour')}</span>
                    </HoursButton>

                    {subbie.manuallyCreated && (
                      <span>
                        <Ellipsis menu={ellipsisMenu(deleteSubbie(index))} />
                      </span>
                    )}
                  </Flexbox>
                </Flexbox>
              )
            })}

            <Flexbox
              justifyContent="space-between"
              alignItems="center"
              gap="0.5rem"
            >
              <Button
                onClick={addSubcontractor}
                style={{ marginTop: '0.5rem' }}
                icon={<IconPlus size={16} />}
              >
                Add another Subcontractor
              </Button>

              <Text secondary="_600">
                Total hours:{' '}
                {calculateTotalHours(
                  value.subcontractorAttendances,
                  value.overrides
                )}
              </Text>
            </Flexbox>

            {!!value.visitors?.length && (
              <div
                className="ant-form-item-label"
                style={{ marginTop: '1rem' }}
              >
                <label htmlFor="visitors">Visitors</label>
                <ul id="visitors">
                  {value.visitors.map((visitor) => (
                    <li key={visitor}>{visitor}</li>
                  ))}
                </ul>
              </div>
            )}
          </Space>
        </Flexbox>
      </Space>

      {selectedSubcontractorId !== undefined && (
        <WorkerAttendanceModal
          value={value}
          onChange={onChange}
          closeModal={closeModal}
          subcontractorId={selectedSubcontractorId}
          hourConfig={hourConfig}
          workers={projectWorkers.filter(
            (w) => w.subcontractorId === selectedSubcontractorId
          )}
          subbieName={
            projectSubcontractors.find((s) => s.id === selectedSubcontractorId)
              .company.name
          }
        />
      )}
    </>
  )
}
